import React, { useCallback } from 'react';
import P from 'prop-types';
// eslint-disable-next-line lodash/import-scope
import property from 'lodash/property';
import { Box, Title, Text } from '@nerdwallet/currency';
import Image from '@nerdwallet/react-image';

import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import classNames from 'classnames';
import {
  boolCheckboxSpec,
  group,
  numberInputSpec,
  stringInputSpec,
  stringSelectSpec,
} from '../../lib/editing-configs';
import getTextColor from '../../lib/text-color-config';
import parseMarkdownLinks from '../../lib/parse-md-links';
import styles from './sweepstakes-form.module.less';

const getTextColorStyle = (textColorOptions, propertyName = 'color') => {
  const { textColor: color } = getTextColor(textColorOptions);
  return { [propertyName]: color };
};

const linkParser = (content) => parseMarkdownLinks(content, false);

const htmlOutputParser = (content, enableHtml = false) => {
  if (enableHtml) {
    // eslint-disable-next-line react/no-danger
    return <span dangerouslySetInnerHTML={{ __html: content }} />;
  }
  return linkParser(content);
};

const iframeContent = (formId) => `
    <script
      src="${formId}">
    </script>
  `;

const SweepstakesForm = ({
  formBackgroundImageDesktop,
  formBackgroundImageMobile,
  formBackgroundImageTablet,
  formId,
  cardIcon,
  cardTitle,
  cardTitleChronicle,
  enableHtmlInCardTitle,
  cardTitleColorOptions,
  cardTitleSize,
  cardSubtitle,
  cardSubtitleColorOptions,
  cardSubtitleSize,
  enableHtmlInCardSubtitle,
  size,
  topFormMargin,
  bottomFormMargin,
  topFormPadding,
  bottomFormPadding,
}) => {
  const getBackgroundImageSrcMap = useCallback(
    (bgSize) => {
      const type = {
        lg: formBackgroundImageDesktop,
        md: formBackgroundImageTablet,
        sm: formBackgroundImageMobile,
      };
      if (bgSize !== 'lg' && !type[bgSize]) {
        return type.lg;
      }
      return type[bgSize];
    },
    [
      formBackgroundImageDesktop,
      formBackgroundImageMobile,
      formBackgroundImageTablet,
    ]
  );

  return (
    <Box
      className={styles.container}
      style={{ backgroundImage: `url(${getBackgroundImageSrcMap(size)})` }}
    >
      <Box
        className={styles.card}
        style={{
          marginTop: topFormMargin ?? 85,
          marginBottom: bottomFormMargin ?? 63,
          paddingTop: topFormPadding ?? 50,
          paddingBottom: bottomFormPadding ?? 50,
        }}
      >
        {cardIcon && (
          <Image
            className={styles.cardIcon}
            src={cardIcon}
            alt="NerdWallet Brand"
            backgroundColor="transparent"
            naturalHeight={318}
            naturalWidth={289}
          />
        )}
        <Box className={styles.cardHeading}>
          <Title
            style={getTextColorStyle(cardTitleColorOptions)}
            className={classNames(styles.cardTitle, [
              { [styles.titleChronicle]: cardTitleChronicle },
            ])}
            size={cardTitleSize}
          >
            {htmlOutputParser(cardTitle, enableHtmlInCardTitle)}
          </Title>
          <Box
            style={getTextColorStyle(cardSubtitleColorOptions)}
            className={styles.cardSubtitle}
          >
            <Text size={cardSubtitleSize} component="p">
              {htmlOutputParser(cardSubtitle, enableHtmlInCardSubtitle)}
            </Text>
          </Box>
        </Box>
        {formId ? (
          <span
            id="formstack-form-container"
            style={{ textAlign: 'left' }}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: iframeContent(formId) }}
          />
        ) : (
          <Box style={{ margin: '2rem 0' }}>
            <Text size="large" bold>
              Enter a Formstack form ID to make a form appear
            </Text>
          </Box>
        )}
      </Box>
    </Box>
  );
};

SweepstakesForm.propTypes = {
  cardTitle: P.string,
  cardTitleSize: P.oneOf(['default', 'small']),
  cardSubtitle: P.string,
  cardSubtitleSize: P.oneOf(['default', 'large', 'small', 'x-small']),
  cardIcon: P.string,
  formBackgroundImageDesktop: P.string,
  formBackgroundImageMobile: P.string,
  formBackgroundImageTablet: P.string,
  topFormMargin: P.number,
  bottomFormMargin: P.number,
  topFormPadding: P.number,
  bottomFormPadding: P.number,
  size: P.string,
  cardTitleChronicle: P.bool,
  enableHtmlInCardTitle: P.bool,
  enableHtmlInCardSubtitle: P.bool,
  bgOptions: P.shape({
    backgroundColor: P.string,
  }),
  cardTitleColorOptions: P.shape({
    hexColor: P.string,
    textColor: P.string,
  }),
  cardSubtitleColorOptions: P.shape({
    hexColor: P.string,
    textColor: P.string,
  }),
  children: P.node,
  formId: P.string,
  formTitle: P.string,
};

SweepstakesForm.defaultProps = {
  formId: '',
  formTitle: '',
};

SweepstakesForm.getEditingConfig = (componentDocument) => [
  group('Title', [
    stringInputSpec('cardTitle', 'Title'),
    stringSelectSpec(
      'cardTitleSize',
      'Title Size',
      [
        { label: 'Small', value: 'small' },
        { label: 'Large', value: 'default' },
      ],
      {
        help: 'Defaults to large',
      }
    ),
    boolCheckboxSpec('cardTitleChronicle', 'Title in Chronicle?'),
    boolCheckboxSpec('enableHtmlInCardTitle', 'Allow HTML in Title?', {
      help: 'WARNING: We strongly advise against making use of this feature unless you really need it.',
    }),
    ...getTextColor.getEditingConfig(
      componentDocument,
      'cardTitleColorOptions',
      'Title Color'
    ),
  ]),
  group('Subtitle', [
    stringInputSpec('cardSubtitle', 'Subtitle'),
    stringSelectSpec(
      `cardSubtitleSize`,
      'Subtitle Size',
      [
        { label: 'Default', value: 'default' },
        { label: 'Large', value: 'large' },
        { label: 'Small', value: 'small' },
        { label: 'X-Small', value: 'x-small' },
      ],
      {
        help: 'Defaults to small',
      }
    ),
    ...getTextColor.getEditingConfig(
      componentDocument,
      'cardSubtitleColorOptions',
      'Subtitle Color'
    ),
    boolCheckboxSpec('enableHtmlInCardSubtitle', 'Allow HTML in Subtitle?', {
      help: 'WARNING: We strongly advise against making use of this feature unless you really need it.',
    }),
  ]),
  group('Formstack', [
    stringInputSpec('formId', 'Formstack form ID', {
      help: 'The Formstack form id is the URL of the Formstack form',
    }),
  ]),
  group('Background', [
    stringInputSpec('cardIcon', 'Card Icon'),
    stringInputSpec(
      'formBackgroundImageDesktop',
      'Background Image URL (Desktop)'
    ),
    stringInputSpec(
      'formBackgroundImageTablet',
      'Background Image URL (Tablet)'
    ),
    stringInputSpec(
      'formBackgroundImageMobile',
      'Background Image URL (Mobile)'
    ),
    numberInputSpec('topFormMargin', 'Top Form Margin'),
    numberInputSpec('bottomFormMargin', 'Bottom Form Margin'),
    numberInputSpec('topFormPadding', 'Top Form Padding'),
    numberInputSpec('bottomFormPadding', 'Bottom Form Padding'),
  ]),
];

SweepstakesForm.displayName = 'SweepstakesForm';
export default connect(
  createStructuredSelector({
    size: property('mediaQuery'),
  })
)(SweepstakesForm);
